<template>
    <div>
        <div class="d-flex justify-content-end mb-3">
            <button class="btn btn-sm btn-outline-primary" @click="$router.push('/dashboard/clients')">
                👤 View Clients
            </button>
        </div>

        <h2>Doctor Orders for {{ student.user?.name || 'Student' }}</h2>
        <h4>PID : {{ student.pid }}</h4> <br>
        <button class="btn btn-sm btn-success me-2" v-if="student && !student.has_doctor_order"
            @click="$router.push(`/dashboard/doctor-orders/create?student_id=${student.id}`)">
            ➕ Add Doctor Order
        </button>

        <button class="btn btn-sm btn-warning" v-if="student && student.has_doctor_order"
            @click="$router.push(`/dashboard/doctor-orders/edit/${student.doctor_order_id}`)">
            ✏️ Update Doctor Order
        </button>

        <button class="btn btn-sm btn-warning ms-2" v-if="student && student.has_doctor_order"
            @click="$router.push(`/dashboard/doctor-orders/edit/${student.doctor_order_id}?updateOnly=yes`)">
            ✏️ Make Corrections
        </button>

        <br>
        <hr class="mt-2" />

        <div v-if="orders.length === 0" class="alert alert-info">
            No doctor orders found for this student.
        </div>

        <div v-for="(order, index) in orders" :key="order.id" class="card mb-4 shadow-sm">
            <div class="card-header d-flex justify-content-between align-items-center bg-light">
                <strong class="text-danger">🕒 Modified: {{ $formatDateTime(order.updated_at) }}</strong>
                <button class="btn btn-sm btn-outline-secondary" v-if="index !== 0" @click="toggleCollapse(index)">
                    {{ collapsed.includes(index) ? '🔽 Expand' : '🔼 Collapse' }}
                </button>
            </div>

            <div class="card-body" v-show="index === 0 || !collapsed.includes(index)">
                <div class="doctor-order-detail">

                    <h5 class="section-title">1. Client Information</h5>
                    <p><strong>Client Name:</strong> {{ order.student?.user?.name }} <strong class="ms-3">Date:</strong>
                        {{ $formatDate(order.date) }}</p>
                    <hr />

                    <h5 class="section-title">2. Reason for Visit or Complaint</h5>
                    <ul>
                        <li v-for="(reason, index) in JSON.parse(order.reason || '[]')" :key="index">
                            {{ reason }}
                        </li>
                    </ul>
                    <p><strong>Findings:</strong> {{ order.findings }}</p>
                    <hr />

                    <h5 class="section-title">4. Plan / Treatment Recommendations</h5>
                    <ul>
                        <li v-for="(p, i) in order.plans" :key="i">{{ p.plan }}</li>
                    </ul>

                    <h5 class="section-title mt-4">Medications including Indications for Medications</h5>
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>Medication</th>
                                <th>Start Date</th>
                                <!-- <th>End Date</th> -->
                                <th>Indications</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style="text-align: left !important;" v-for="(m, i) in order.medications" :key="i">
                                <td>{{ m.name }}</td>
                                <td>{{ $formatDate(m.start_date) }}</td>
                                <!-- <td>{{ $formatDate(m.end_date) }}</td> -->
                                <td>{{ m.indication }}</td>
                            </tr>
                        </tbody>
                    </table>

                    <h5 class="section-title mt-4">Standing Orders for Clients in Intensive Outpatient</h5>
                    <ul class="standing-orders">
                        <li v-for="(medication, i) in allMedications" :key="i">
                            <strong>{{ medication.name }}</strong> {{ medication.symptom_treated }}, {{
                                medication.frequency_dosage }}
                        </li>
                    </ul>

                    <div class="yes-no-section mt-4">
                        <p>
                            May Client continue to take medication from standing orders?
                            <strong>{{ order.allow_standing_orders ? 'Yes' : 'No' }}</strong>
                        </p>
                        <p>
                            Does client have permission to take medications upon discharge?
                            <strong>{{ order.discharge_meds_permission ? 'Yes' : 'No' }}</strong>
                        </p>
                        <p>
                            May Client continue to all previous orders?
                            <strong>{{ order.continue_previous_orders ? 'Yes' : 'No' }}</strong>
                        </p>
                    </div>
                </div>

                <div>
                    <div><strong>Practitioner Signature:</strong> <span
                            :class="{ 'signature-font': order.practitioner_id !== 0 && order.practitioner_id !== null }"><u>
                                {{ order.practitioner?.user?.name || '________________' }}</u>
                        </span> Date:
                        {{ $formatDate(order.signed_at) || '-' }}
                    </div>
                    <button v-if="$canAccess('PractitionerSignature')" class="btn btn-sm btn-outline-primary"
                        @click="$router.push({ name: 'PractitionerSignature', params: { id: order.id } })">
                        🖊️ Practitioner Signature
                    </button>


                </div> <!-- <div class="d-flex justify-content-end mt-4">
                    <button class="btn btn-sm btn-outline-primary" @click="$router.push('/dashboard/clients')">
                        👤 View Clients
                    </button>
                </div> -->
            </div>
            <div class="text-end mt-3 mb-2 me-3">
                <button class="btn btn-sm btn-outline-success"
                    @click="downloadPDF(order.id, student?.user?.name, student?.pid)">
                    📥 Download PDF
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: ['id'],
    data() {
        return {
            orders: [],
            student: {},
            allMedications: [],
            collapsed: [] // holds indexes of collapsed doctor orders
        };
    },
    methods: {
        async downloadPDF(id, name, pid) {
            try {
                const response = await this.$api.get(`/doctor-orders/${id}/export-pdf`, {
                    responseType: 'blob' // 🔥 this is important for PDF download
                });
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `doctor-order-${name}-${pid}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error('Download failed', error);
                this.$toast?.error('Failed to download PDF');
            }
        }
        ,
        async fetchOrders() {
            const res = await this.$api.get(`/students/${this.id}`);
            this.student = res.data;
            const ordersRes = await this.$api.get(`/doctor-orders?student_id=${this.id}`);
            this.orders = ordersRes.data;
        },
        async fetchMedications() {
            const medRes = await this.$api.get('/medications');
            this.allMedications = medRes.data;
        },
        toggleCollapse(index) {
            if (this.collapsed.includes(index)) {
                this.collapsed = this.collapsed.filter(i => i !== index);
            } else {
                this.collapsed.push(index);
            }
        }
    },
    async mounted() {
        await this.fetchOrders();
        await this.fetchMedications();
        this.collapsed = this.orders.map((_, i) => i).filter(i => i !== 0);
    }

};
</script>

<style>
.section-title {
    font-weight: bold;
    color: #004080;
    margin-top: 20px;
}

.standing-orders li {
    margin-bottom: 8px;
    line-height: 1.6;
}
</style>
